import { config } from "@/shared/lib/config";
import { formatDateForInput } from "@/shared/lib/date-time";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, Card, DatePicker } from "@devhop/ui";

type DepartmentItem = { id: string; name: string };

const LEVEL_OPTIONS = [
  { value: "__all__", label: "ທັງໝົດ" },
  { value: "organization", label: "ລະດັບອົງກອນ" },
  { value: "department", label: "ລະດັບຜະແນກ" },
  { value: "position", label: "ລະດັບຕຳແໜ່ງ" },
] as const;

type ProjectSummaryFilterProps = {
  departmentId: string;
  level: string;
  startDateFrom: string;
  startDateTo: string;
  onDepartmentChange: (value: string) => void;
  onLevelChange: (value: string) => void;
  onStartDateFromChange: (value: string) => void;
  onStartDateToChange: (value: string) => void;
  onApply: () => void;
};

export function ProjectSummaryFilter({
  departmentId,
  level,
  startDateFrom,
  startDateTo,
  onDepartmentChange,
  onLevelChange,
  onStartDateFromChange,
  onStartDateToChange,
  onApply,
}: ProjectSummaryFilterProps) {
  const handleRange = (
    range: { from?: Date; to?: Date } | Date[] | undefined,
  ) => {
    const from: Date | undefined = Array.isArray(range)
      ? range[0]
      : range?.from;
    const to: Date | undefined = Array.isArray(range) ? range[1] : range?.to;
    onStartDateFromChange(from ? formatDateForInput(from) : "");
    onStartDateToChange(to ? formatDateForInput(to) : "");
  };

  return (
    <Card className="flex flex-col gap-0 p-4">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
        <div className="flex flex-col gap-0">
          <div className="font-medium text-sm">ພະແນກ</div>
          <InfiniteCombobox<DepartmentItem>
            queryKey={["project-summary-departments"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={departmentId}
            onValueChange={onDepartmentChange}
            placeholder="ທຸກພະແນກ"
            clearable
          />
        </div>
        <div className="flex flex-col gap-0">
          <div className="font-medium text-sm">ລະດັບໂຄງການ</div>
          <SimpleSelect
            options={[...LEVEL_OPTIONS]}
            value={level}
            onValueChange={onLevelChange}
            placeholder="ທັງໝົດ"
            className="w-full"
            aria-label="ລະດັບໂຄງການ"
          />
        </div>
        <div className="flex flex-col gap-0 sm:col-span-2 lg:col-span-2">
          <div className="font-medium text-sm">ວັນເລີ່ມໂຄງການ (ຊ່ວງວັນທີ)</div>
          <DatePicker
            mode="range"
            value={{
              from: startDateFrom ? new Date(startDateFrom) : undefined,
              to: startDateTo ? new Date(startDateTo) : undefined,
            }}
            onChange={handleRange}
            placeholder="ເລືອກຊ່ວງວັນທີ"
            className="w-full"
          />
        </div>
        <div className="flex items-end sm:col-span-2 lg:col-span-1">
          <Button type="button" onClick={onApply} className="w-full">
            ກວດສອບ
          </Button>
        </div>
      </div>
    </Card>
  );
}
